/**
 * Demo app previews: the presenter page's "Run on" iPhone / Android modes.
 *
 * The demo host asks the backend to start a live preview of a school app on the
 * mobile lab (tooling/mobile-lab, `previews.ts`), signed in as one demo persona.
 * The backend mints the persona's ticket through the demo mint pipeline
 * (allowlist, caps, refusal budget, ledger) and hands it to the lab directly:
 * the ticket never reaches the host or the browser.
 *
 *   | Route                                               | Grant             | Answer                          |
 *   |-----------------------------------------------------|-------------------|---------------------------------|
 *   | POST /internal/demo/app-preview                     | presenter or link | {@link DemoAppPreviewResponse}  |
 *   | GET  /internal/demo/app-preview/:previewId          | presenter or link | {@link DemoAppPreviewResponse}  |
 *   | POST /internal/demo/app-preview/:previewId/end      | presenter or link | {@link DemoAppPreviewResponse}  |
 *
 * The host mirrors them at `/api/app-preview`, `/api/app-preview/:id` and
 * `/api/app-preview/:id/end`, adding `hostOrigin` itself as `/api/mint` does.
 *
 * A preview is readable and endable only by the actor that started it: the lab
 * echoes `requestedBy`, which the backend sets to {@link demoAppPreviewRequester}.
 *
 * `viewerUrl` is a capability URL: whoever holds it can watch and tap that one
 * device until the preview ends. The host frames it on the stage and shows it
 * as a QR code for "Your phone"; it is never logged.
 */

import { z } from 'zod'
import {
  AppPreviewEngineSchema,
  AppPreviewPlatformSchema,
  AppPreviewSchema,
  type AppPreviewEngine
} from '../app-preview'

/** Where the device runs: our lab's simulators, or an EAS Simulator session (iOS only). */
export const DemoAppPreviewEngineSchema = AppPreviewEngineSchema
export type DemoAppPreviewEngine = AppPreviewEngine

/**
 * How long the persona's sign-in ticket lives for a preview. Longer than a
 * frame mint's 120 s: the lab hands the ticket over only once the app is
 * installed, and a school's first preview of a release waits for its build
 * (the lab allows an hour). Matches the dashboard's 90 minutes.
 */
export const DEMO_APP_PREVIEW_TICKET_TTL_SECONDS = 90 * 60

/** A lab preview id, `p<yyyymmdd>-<6 hex>`. */
export const DemoAppPreviewIdSchema = z.string().regex(/^p[0-9]{8}-[0-9a-f]{6}$/)

const DemoAppPreviewFieldsSchema = z.object({
  personaId: z.string().min(1).max(191).describe('UsersOnSchools public id of the persona'),
  platform: AppPreviewPlatformSchema,
  engine: DemoAppPreviewEngineSchema,
  /** A registered school app to run instead of the demo school's own. */
  schoolAppId: z.string().min(1).max(100).optional(),
  hostOrigin: z.string().url()
})

const easIsIosOnly = (body: { platform: string; engine: string }) => body.engine !== 'eas' || body.platform === 'ios'
const EAS_IOS_ONLY = { message: 'The Expo engine runs iOS only', path: ['engine'] }

/** The backend's body. */
export const DemoAppPreviewRequestSchema = DemoAppPreviewFieldsSchema.refine(easIsIosOnly, EAS_IOS_ONLY)
export type DemoAppPreviewRequest = z.infer<typeof DemoAppPreviewRequestSchema>

/** What the browser sends the host: the host adds `hostOrigin`, never trusting the client for it. */
export const DemoAppPreviewHostRequestSchema = DemoAppPreviewFieldsSchema.omit({ hostOrigin: true }).refine(
  easIsIosOnly,
  EAS_IOS_ONLY
)
export type DemoAppPreviewHostRequest = z.infer<typeof DemoAppPreviewHostRequestSchema>

export const DemoAppPreviewSchema = AppPreviewSchema.extend({ engine: DemoAppPreviewEngineSchema })
export type DemoAppPreview = z.infer<typeof DemoAppPreviewSchema>

export const DemoAppPreviewResponseSchema = z.object({ preview: DemoAppPreviewSchema })
export type DemoAppPreviewResponse = z.infer<typeof DemoAppPreviewResponseSchema>

/**
 * The `requestedBy` the lab records for a demo preview: `demo:` plus the
 * presenter's Clerk user id or the link's public id. The prefix keeps a demo
 * actor's previews apart from the same person's dashboard previews, and names
 * the source in the lab's history.
 */
export function demoAppPreviewRequester(actorId: string): string {
  return `demo:${actorId}`
}
